import * as THREE from 'three';

function collectMeshes(object: THREE.Object3D) {
  const meshes: THREE.Mesh[] = [];
  let total = 0;
  object.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry.attributes.position) {
      meshes.push(mesh);
      total += mesh.geometry.attributes.position.count;
    }
  });
  return { meshes, total };
}

/** Samples up to `max` vertices of `object`, expressed in `frame`'s local space. */
export function sampleVertices(object: THREE.Object3D, frame: THREE.Object3D, max = 20000): Float32Array {
  frame.updateMatrixWorld(true);
  const inv = frame.matrixWorld.clone().invert();
  const { meshes, total } = collectMeshes(object);
  const stride = Math.max(1, Math.ceil(total / max));
  const out: number[] = [];
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  meshes.forEach((mesh) => {
    m.multiplyMatrices(inv, mesh.matrixWorld);
    const pos = mesh.geometry.attributes.position;
    for (let i = 0; i < pos.count; i += stride) {
      v.fromBufferAttribute(pos, i).applyMatrix4(m);
      out.push(v.x, v.y, v.z);
    }
  });
  return new Float32Array(out);
}

/** Protected-surface samples: normals (in `frame` space) and their protection weight. */
export type ProtectedSurface = {normals: Float32Array;weights: Float32Array;total: number;};

/**
 * Collects the normals of every painted (protected or soft-preference) vertex so the
 * orienter can keep them from facing the platform. Returns null if nothing is protected.
 */
export function sampleProtectedSurface(object: THREE.Object3D, frame: THREE.Object3D, max = 20000): ProtectedSurface | null {
  frame.updateMatrixWorld(true);
  const inv = frame.matrixWorld.clone().invert();
  const { meshes } = collectMeshes(object);
  const normals: number[] = [];
  const weights: number[] = [];
  const n = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const nm = new THREE.Matrix3();
  let protectedCount = 0;
  meshes.forEach((mesh) => {
    const p = mesh.geometry.getAttribute('protect');
    if (!p) return;
    for (let i = 0; i < p.count; i++) if (p.getX(i) > 0.3) protectedCount++;
  });
  if (protectedCount === 0) return null;
  const stride = Math.max(1, Math.ceil(protectedCount / max));
  let seen = 0;
  meshes.forEach((mesh) => {
    const g = mesh.geometry;
    const p = g.getAttribute('protect');
    if (!p) return;
    if (!g.attributes.normal) g.computeVertexNormals();
    const nor = g.attributes.normal;
    m.multiplyMatrices(inv, mesh.matrixWorld);
    nm.getNormalMatrix(m);
    for (let i = 0; i < p.count; i++) {
      const w = p.getX(i);
      if (w <= 0.3) continue;
      if (seen++ % stride !== 0) continue;
      n.fromBufferAttribute(nor, i).applyMatrix3(nm).normalize();
      normals.push(n.x, n.y, n.z);
      weights.push(w >= 0.9 ? 1 : 0.4);
    }
  });
  const w = new Float32Array(weights);
  return { normals: new Float32Array(normals), weights: w, total: w.reduce((s, x) => s + x, 0) };
}

/** Axis-aligned bounds of the samples after rotating them by `q`. */
export function rotatedBounds(samples: Float32Array, q: THREE.Quaternion) {
  const v = new THREE.Vector3();
  const min = new THREE.Vector3(Infinity, Infinity, Infinity);
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
  for (let i = 0; i < samples.length; i += 3) {
    v.set(samples[i], samples[i + 1], samples[i + 2]).applyQuaternion(q);
    min.min(v);
    max.max(v);
  }
  return { min, max };
}

const candidates: THREE.Vector3[] = (() => {
  const list: THREE.Vector3[] = [];
  const axes = [new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 1)];
  axes.forEach((a) => list.push(a.clone(), a.clone().negate()));
  for (let i = 0; i < 3; i++) {
    for (let j = i + 1; j < 3; j++) {
      for (const si of [1, -1]) {
        for (const sj of [1, -1]) {
          list.push(axes[i].clone().multiplyScalar(si).add(axes[j].clone().multiplyScalar(sj)).normalize());
        }
      }
    }
  }
  return list;
})();

/** Surfaces whose normal points this far down will receive supports. */
const SUPPORTED_NORMAL_Y = -0.3;
const PROTECT_WEIGHT = 2.5;

/** Share of protected surface (0–1) that would face the platform — and so get supports — under `q`. */
function protectedExposure(surface: ProtectedSurface, q: THREE.Quaternion) {
  const n = new THREE.Vector3();
  let exposed = 0;
  for (let i = 0; i < surface.weights.length; i++) {
    n.fromArray(surface.normals, i * 3).applyQuaternion(q);
    if (n.y < SUPPORTED_NORMAL_Y) exposed += surface.weights[i];
  }
  return exposed / (surface.total || 1);
}

export type OrientationResult = {quaternion: THREE.Quaternion;protectedExposure: number | null;};

/**
 * Picks the side of the model with the densest geometry (gear teeth and other fine
 * detail carry the most vertices) and turns it to face the build platform, then adds a
 * PreForm-style tilt so large flat areas don't peel in a single layer.
 *
 * When surfaces are protected, every candidate (including the mirrored tilt) is also
 * scored by how much protected area would face down. Protection outweighs detail, so the
 * winner keeps painted areas pointing up or sideways, where no supports are needed.
 */
export function computeAutoOrientation(samples: Float32Array, surface: ProtectedSurface | null = null, tiltDeg = 18): OrientationResult {
  const n = samples.length / 3;
  const tiltAxis = new THREE.Vector3(1, 0, 0.35).normalize();
  const tilts = (surface ? [1, -1] : [1]).map((s) => new THREE.Quaternion().setFromAxisAngle(tiltAxis, THREE.MathUtils.degToRad(tiltDeg * s)));
  let best: OrientationResult = { quaternion: new THREE.Quaternion(), protectedExposure: null };
  let bestScore = -Infinity;
  for (const d of candidates) {
    let lo = Infinity;
    let hi = -Infinity;
    for (let i = 0; i < samples.length; i += 3) {
      const p = samples[i] * d.x + samples[i + 1] * d.y + samples[i + 2] * d.z;
      if (p < lo) lo = p;
      if (p > hi) hi = p;
    }
    const threshold = hi - (hi - lo) * 0.2;
    let count = 0;
    for (let i = 0; i < samples.length; i += 3) {
      if (samples[i] * d.x + samples[i + 1] * d.y + samples[i + 2] * d.z >= threshold) count++;
    }
    const detail = count / n;
    const faceDown = new THREE.Quaternion().setFromUnitVectors(d, new THREE.Vector3(0, -1, 0));
    for (const tilt of tilts) {
      const q = tilt.clone().multiply(faceDown);
      const exposure = surface ? protectedExposure(surface, q) : null;
      const score = detail - (exposure ?? 0) * PROTECT_WEIGHT;
      if (score > bestScore) {
        bestScore = score;
        best = { quaternion: q, protectedExposure: exposure };
      }
    }
  }
  return best;
}
